import {endOfDay, startOfDay, subDays} from 'date-fns';
import {BigCalendarUser} from 'platform/big-calendar';
import {ThemeColorPath, ThemeIconKey} from 'platform/foundation';

import {CalendarEventResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {isNotNilOrEmpty, Nullish, parseDate} from 'shared/web';

import {OutlookCalendarEvent} from './OutlookCalendarEvent';

/**
 * Outlook-born events share one colour instead of the per-user palette that tasks and test
 * drives use: they are read-only mirrors, so their origin is what distinguishes them, not
 * their owner. Green keeps them apart from the teal test drives and the user colours.
 */
const OUTLOOK_EVENT_COLOR: ThemeColorPath = 'palettes.green.70.100';
const OUTLOOK_EVENT_ICON: ThemeIconKey = 'action/calendar_today';

/** Length of the `YYYY-MM-DD` prefix of an ISO 8601 timestamp. */
const ISO_DATE_LENGTH = 10;

/**
 * Calendar day an all-day boundary falls on, read from the date part of the timestamp so it does
 * not move with the viewer's timezone. All-day boundaries arrive as UTC midnight, which a plain
 * `Date` resolves to the previous day for anyone west of UTC.
 */
const parseCalendarDay = (isoTimestamp: string) =>
  parseDate(isoTimestamp.slice(0, ISO_DATE_LENGTH));

/**
 * Converts standalone Outlook calendar events into BigCalendar events.
 *
 * Microsoft Graph reports the end of an all-day event exclusively (5.–8. 5. arrives as ending
 * 9. 5. 00:00), so an all-day span is pulled back by one day and clamped to whole days. Its
 * boundaries are read as calendar days rather than instants, so the span covers the days the
 * organiser picked in every viewer timezone. Timed events keep the boundaries the API returned.
 *
 * The events are already filtered to the picked users by the API; the membership check mirrors
 * the task and test-drive sources so a user removed from the picker disappears immediately
 * instead of on the next refetch.
 */
export function getBigCalendarDataFromCalendarEvents(
  events: CalendarEventResponseBody[] | Nullish,
  selectedUsers: BigCalendarUser[] | Nullish
) {
  return events?.reduce<OutlookCalendarEvent[]>((calendarEvents, event) => {
    const isUserSelected = selectedUsers?.some((user) => user.id === event.userId) ?? false;

    if (!isUserSelected) {
      return calendarEvents;
    }

    const startsAt = event.isAllDay ? parseCalendarDay(event.startsAt) : parseDate(event.startsAt);
    const endsAt = event.isAllDay ? parseCalendarDay(event.endsAt) : parseDate(event.endsAt);

    if (!startsAt || !endsAt) {
      return calendarEvents;
    }

    calendarEvents.push({
      id: event.id,
      title: isNotNilOrEmpty(event.title)
        ? event.title
        : i18n.t('entity.calendarEvent.labels.untitled'),
      start: event.isAllDay ? startOfDay(startsAt) : startsAt,
      end: event.isAllDay ? endOfDay(subDays(endsAt, 1)) : endsAt,
      isAllDay: event.isAllDay,
      isReadOnly: true,
      color: OUTLOOK_EVENT_COLOR,
      icon: OUTLOOK_EVENT_ICON,
      eventType: 'outlookEvent',
      userId: event.userId,
      location: event.location,
      description: event.description,
    });

    return calendarEvents;
  }, []);
}
